import { RelBox } from '../../../api/ai.api.types';

// The single target frame of the whole flow: where the meter should be placed (viewport-
// relative). Single source of truth for both the rendered corner brackets and the alignment
// scoring. Recognition works reliably with the meter (not its display) framed, so the capture
// phase keeps this frame too — only label and instructions change.
export const TARGET_FRAME: RelBox = { x: 0.1, y: 0.2, w: 0.8, h: 0.45 };

// Meters are taller than wide (except round water meters), so the target frame aims for a fixed
// 3:4 pixel aspect (w:h). RelBox units are viewport-relative — a static RelBox would render
// square or landscape depending on the device, so the frame is computed from the actual
// viewport: 3:4 in pixels, capped at 85 % width / 58 % height, horizontally centered, top at
// 17 % so the hint row below the frame keeps room above the panels.
const TARGET_ASPECT_W_TO_H = 0.75;
const TARGET_MAX_REL_WIDTH = 0.85;
const TARGET_MAX_REL_HEIGHT = 0.58;
const TARGET_TOP = 0.17;

export const computeTargetFrame = (viewport: { width: number; height: number }): RelBox => {
    if (viewport.width <= 0 || viewport.height <= 0) {
        return TARGET_FRAME;
    }
    let widthPx = viewport.width * TARGET_MAX_REL_WIDTH;
    let heightPx = widthPx / TARGET_ASPECT_W_TO_H;
    const maxHeightPx = viewport.height * TARGET_MAX_REL_HEIGHT;
    if (heightPx > maxHeightPx) {
        heightPx = maxHeightPx;
        widthPx = heightPx * TARGET_ASPECT_W_TO_H;
    }
    const w = widthPx / viewport.width;
    const h = heightPx / viewport.height;
    return { x: (1 - w) / 2, y: TARGET_TOP, w, h };
};

// One directive at a time, following scanner-UX practice (Scanbot/KYC flows): position first,
// then distance, then "hold still" — never several competing instructions at once.
export type AlignmentHint = 'noMeter' | 'moveLeft' | 'moveRight' | 'moveUp' | 'moveDown' | 'moveCloser' | 'moveBack' | 'holdStill';

export interface Alignment {
    // 0..1 — drives the white→green color ramp and the progress bar.
    score: number;
    hint: AlignmentHint;
}

// Center offset (relative to the frame size) beyond which a directional hint is shown.
const CENTER_HINT_THRESHOLD = 0.18;
// Meter area relative to the frame area: below IDEAL_MIN → "move closer", above IDEAL_MAX →
// "move back". Between them the size is considered good.
// 0.55 of the frame area (~74 % edge length): strict enough that "snapped" implies crops are
// sharp — the snap doubles as the reading gate.
// Note: SIZE_IDEAL_MIN is where the size score saturates, not a hard floor — with the 0.55/0.45
// position/size weighting, a perfectly centered box can snap from ~0.31 of the frame area. The
// device protocol validates the felt strictness.
const SIZE_IDEAL_MIN = 0.55;
const SIZE_IDEAL_MAX = 1.1;
// Center distance (relative units) at which the position score reaches 0.
const POSITION_SCORE_RANGE = 0.6;

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

// Hysteresis helper: maintains a binary state that enters at a higher threshold and exits at a
// lower one, preventing flutter around the boundary.
const withHysteresis = (score: number, isActive: boolean, enterThreshold: number, exitThreshold: number): boolean =>
    isActive ? score >= exitThreshold : score >= enterThreshold;

// "Reached" state: once the alignment is good enough the frame celebrates (glow/pulse/vibration)
// — with hysteresis so the state doesn't flutter around the threshold. The target frame itself
// stays FIXED at all times: a frame that moves toward the detection would destroy the very
// clarity ("put the meter THERE") it exists to provide.
const SNAP_ENTER_SCORE = 0.8;
const SNAP_EXIT_SCORE = 0.62;

export const computeSnap = (score: number, wasSnapped: boolean): boolean =>
    withHysteresis(score, wasSnapped, SNAP_ENTER_SCORE, SNAP_EXIT_SCORE);

// Reading gate: value/number extraction only starts once the meter sits firmly in the target
// frame. Reading starts earlier (0.7) than the visual snap (0.8) — the recognition warms up
// during the final approach while the saturated-green snap stays the "perfectly placed" reward.
// Feed this with computeReadingGateScore (NOT the visual alignment score): the gate exists to
// keep blurry far-away crops out of the consensus, so it must never close for "too close".
const READING_GATE_ENTER_SCORE = 0.7;
const READING_GATE_EXIT_SCORE = 0.55;

export const computeReadingGate = (score: number, wasOpen: boolean): boolean =>
    withHysteresis(score, wasOpen, READING_GATE_ENTER_SCORE, READING_GATE_EXIT_SCORE);

// Progress emphasis for the accent ramp: squaring keeps the color near-white while the meter
// is far off and accelerates the shift to green as the alignment improves — the per-step
// color gain grows, which nudges users to keep improving until the snap. The linear score
// stays in use where actual progress is displayed (progress bar).
export const emphasizedScore = (score: number): number => score * score;

// White → green ramp following the score (scanner-UX convention: neutral while searching,
// increasingly confident color as the alignment improves). Shared by brackets, progress bar and
// the particle stream.
export const rampColor = (score: number): string => {
    const r = Math.round(255 + (74 - 255) * score);
    const g = Math.round(255 + (222 - 255) * score);
    const b = Math.round(255 + (128 - 255) * score);
    return `rgb(${r}, ${g}, ${b})`;
};

// Shared position/size metrics for both scores below.
const boxMetrics = (meterBox: RelBox, target: RelBox) => {
    const offsetX = (meterBox.x + meterBox.w / 2 - (target.x + target.w / 2)) / target.w;
    const offsetY = (meterBox.y + meterBox.h / 2 - (target.y + target.h / 2)) / target.h;
    const sizeRatio = (meterBox.w * meterBox.h) / (target.w * target.h);
    const positionScore = 1 - clamp01(Math.hypot(offsetX, offsetY) / POSITION_SCORE_RANGE);
    return { offsetX, offsetY, sizeRatio, positionScore };
};

// Reading-gate score: like the visual alignment score, but WITHOUT the "too large" penalty.
// The gate exists to keep blurry far-away crops out of the value consensus; a meter that
// OVERFILLS the frame is the opposite case — close, large, sharp — so value/number extraction
// and the hi-res detail reads must keep running there. Visually, "too close" still costs the
// snap and shows "move back" (computeAlignment below), but reading never stops because of it.
export const computeReadingGateScore = (meterBox: RelBox | null, target: RelBox = TARGET_FRAME): number => {
    if (!meterBox || meterBox.w <= 0 || meterBox.h <= 0) {
        return 0;
    }
    const { sizeRatio, positionScore } = boxMetrics(meterBox, target);
    const sizeScore = sizeRatio < SIZE_IDEAL_MIN ? sizeRatio / SIZE_IDEAL_MIN : 1;
    return clamp01(0.55 * positionScore + 0.45 * clamp01(sizeScore));
};

// Recognition overrides alignment nagging (#53): once the reading gate is open the value/number
// extraction is already running and the overlay shows "recognized", so a competing directional
// hint ("move back"/"move right") only confuses. In that state the single sensible directive is
// to hold still — keeping text, audio ("hold still") and overlay ("recognized") consistent.
export const resolveGuidanceHint = (hint: AlignmentHint, readingGateOpen: boolean): AlignmentHint => {
    return readingGateOpen ? 'holdStill' : hint;
};

// Scores how well the (tracked) meter box is placed inside the target frame and derives the
// single most useful instruction. The camera follows the meter: when the meter sits right of the
// frame center, panning the camera right moves it toward the center — hence 'moveRight'.
export const computeAlignment = (meterBox: RelBox | null, target: RelBox = TARGET_FRAME): Alignment => {
    if (!meterBox || meterBox.w <= 0 || meterBox.h <= 0) {
        return { score: 0, hint: 'noMeter' };
    }

    const { offsetX, offsetY, sizeRatio, positionScore } = boxMetrics(meterBox, target);
    const sizeScore = sizeRatio < SIZE_IDEAL_MIN ? sizeRatio / SIZE_IDEAL_MIN : sizeRatio > SIZE_IDEAL_MAX ? SIZE_IDEAL_MAX / sizeRatio : 1;
    const score = clamp01(0.55 * positionScore + 0.45 * clamp01(sizeScore));

    let hint: AlignmentHint = 'holdStill';
    if (Math.abs(offsetX) > CENTER_HINT_THRESHOLD || Math.abs(offsetY) > CENTER_HINT_THRESHOLD) {
        if (Math.abs(offsetX) >= Math.abs(offsetY)) {
            hint = offsetX > 0 ? 'moveRight' : 'moveLeft';
        } else {
            hint = offsetY > 0 ? 'moveDown' : 'moveUp';
        }
    } else if (sizeRatio < SIZE_IDEAL_MIN) {
        hint = 'moveCloser';
    } else if (sizeRatio > SIZE_IDEAL_MAX) {
        hint = 'moveBack';
    }

    return { score, hint };
};
